import type {
  AccessToken,
  AdCampaignsV2,
  AdImagesUploadResponse,
  AdsCreationResponse,
  AdSearchResponse,
  AdVideoStatusResponse,
  AdVideosUploadResponse,
  FacebookPageDisplay,
  GetBrandsBrandIdInstagramProductTagsCatalogsParams,
  GetBrandsBrandIdInstagramProductTagsSearchProductsParams,
  InstagramProduct,
  InstagramProductSearchResponse,
  ListCampaignV2InsightsResponse,
  ListFacebookPagesResponse,
  ListPublicPagesResponse,
  VideoAdsCreationResponse,
} from '@dashhudson/api-client/facebook/model';
import type {
  FacebookBusiness,
  GetAccessTokensRequiredType,
  GetLocationsLocationFieldsItem,
  GetLocationsLocationType,
  InstagramShoppingTagsEligibilityResponse,
  InstagramShoppingTagsProductCatalogResponse,
  ListFacebookBusinessCatalogsResponse,
  Location,
} from '@dashhudson/api-client/facebook/model/raw';

import {
  getDeleteBrandsBrandIdAdAccountsFbAccountIdUrl,
  getGetBrandsBrandIdAdAccountsUrl,
  getGetBrandsBrandIdAdAccountsV2Url,
  getGetBrandsBrandIdCampaignsUrl,
  getGetBrandsBrandIdFbBusinessesUrl,
  getGetBrandsBrandIdInstagramProductTagsEligibilityUrl,
  getGetBrandsBrandIdMarketingV2AdvideosVideoIdStatusUrl,
  getGetBrandsBrandIdMetaOpportunityScoreUrl,
  getGetBrandsBrandIdPageUrl,
  getGetPagesUrl,
  getPatchBrandsBrandIdAdAccountsFbAccountIdUrl,
  getPostBrandsBrandIdMarketingV2AdimagesUrl,
  getPostBrandsBrandIdMarketingV2AdsSearchUrl,
  getPostBrandsBrandIdMarketingV2AdsUrl,
  getPostBrandsBrandIdMarketingV2AdvideosUrl,
  getPostBrandsBrandIdMarketingV2CampaignsSearchUrl,
  getPostBrandsBrandIdMarketingV2VideoAdsUrl,
  getPutBrandsAdAccountsUrl,
  getPutBrandsMarketingV2CampaignsUrl,
  getPutBrandsPagesUrl,
} from '@dashhudson/api-client/facebook/client';
import merge from 'lodash/merge';

import { createDashHttpClient, type HttpRequestConfig } from '@/apis/dash.http';
import { env } from '@/env';

const http = createDashHttpClient(env.facebookApiUrl, {
  camelizeKeys: true,
});

export const oldHttp = createDashHttpClient(env.facebookApiUrl);

// Meta ad-account ids are opaque digit strings that would lose precision through Number(); the
// builders only interpolate their path params, so the raw string passes through unchanged.
const adAccountPathId = (fbAccountId: string) => fbAccountId as unknown as number;

// Facebook often returns an after cursor item when it should not
const correctPagingCursor = <
  T extends { data?: { data?: unknown[]; paging?: { cursors?: { after?: string | null } } } },
>(
  response: T,
): T => {
  const { data } = response.data ?? {};
  if (data?.length === 0 && response.data?.paging?.cursors) {
    response.data.paging.cursors.after = null;
  }
  return response;
};

// ----- Endpoints not in the OpenAPI spec (responses left untyped) -----

export async function getFacebookPage(
  { brandId }: { brandId: number | string },
  config: HttpRequestConfig = {},
) {
  return http.get<FacebookPageDisplay>(getGetBrandsBrandIdPageUrl(Number(brandId)), config);
}

export async function getCampaignList({ brandId }: { brandId: number | string }) {
  return oldHttp.get(getGetBrandsBrandIdCampaignsUrl(Number(brandId)));
}

export async function getAccountsFromApi(
  {
    brandId,
    fetchApi,
    connectFacebookAdsToken,
    isMonitored,
  }: {
    brandId: number | string;
    fetchApi?: boolean;
    connectFacebookAdsToken?: string;
    isMonitored?: boolean;
  },
  config: HttpRequestConfig = {},
) {
  return oldHttp.get(
    getGetBrandsBrandIdAdAccountsUrl(Number(brandId)),
    merge(
      {
        params: {
          fetchApi,
          connectFacebookAdsToken,
          isMonitored,
        },
      },
      config,
    ),
  );
}

export async function getAccountsFromApiV2(
  {
    brandId,
    connectFacebookAdsToken,
  }: { brandId: number | string; connectFacebookAdsToken?: string },
  config: HttpRequestConfig = {},
) {
  return oldHttp.get(
    getGetBrandsBrandIdAdAccountsV2Url(Number(brandId)),
    merge(
      {
        params: {
          connectFacebookAdsToken,
        },
      },
      config,
    ),
  );
}

export async function getAccountsMultiBrandsFromApi(
  { brandIds }: { brandIds: number[] },
  config: HttpRequestConfig = {},
) {
  return oldHttp.put(getPutBrandsAdAccountsUrl(), { brand_ids: brandIds }, config);
}

export async function getAllFacebookPages(
  { brandIds }: { brandIds: number[] },
  config: HttpRequestConfig = {},
) {
  return http.put<ListFacebookPagesResponse>(
    getPutBrandsPagesUrl(),
    { brand_ids: brandIds },
    config,
  );
}

export async function updateAccount({
  brandId,
  fbAccountId,
  isMonitored,
}: {
  brandId: number | string;
  fbAccountId: string;
  isMonitored?: boolean;
}) {
  return oldHttp.patch(
    getPatchBrandsBrandIdAdAccountsFbAccountIdUrl(Number(brandId), adAccountPathId(fbAccountId)),
    { is_monitored: isMonitored },
  );
}

export async function deleteAccount({
  brandId,
  fbAccountId,
}: {
  brandId: number | string;
  fbAccountId: string;
}) {
  return oldHttp.delete(
    getDeleteBrandsBrandIdAdAccountsFbAccountIdUrl(Number(brandId), adAccountPathId(fbAccountId)),
  );
}

export async function getMetaOpportunityScore(
  { brandId }: { brandId: number | string },
  config: HttpRequestConfig = {},
) {
  return http.get(getGetBrandsBrandIdMetaOpportunityScoreUrl(Number(brandId)), merge({}, config));
}

// ----- Endpoints documented in the OpenAPI spec -----

export async function getPages(
  { ids, q, limit }: { ids?: string; q?: string; limit?: number },
  config: HttpRequestConfig = {},
) {
  return http.get<ListPublicPagesResponse>(
    getGetPagesUrl(),
    merge({ params: { ids, q, limit } }, config),
  );
}

export async function getBusinesses(
  { brandId, params }: { brandId: number | string; params?: Record<string, unknown> },
  config: HttpRequestConfig = {},
) {
  return oldHttp.get<FacebookBusiness[]>(getGetBrandsBrandIdFbBusinessesUrl(Number(brandId)), {
    ...config,
    params,
  });
}

export async function getBusinessCatalogs(
  {
    brandId,
    params,
    paging = {},
  }: {
    brandId: number | string;
    params?: { business_id?: string; [key: string]: unknown };
    paging?: { cursors?: { after?: string | null } };
  },
  config: HttpRequestConfig = {},
) {
  const localParams: Record<string, unknown> = { ...params };
  const after = paging?.cursors?.after;
  if (after) {
    localParams.after = after;
  }

  const url = `/brands/${brandId}/fb_business_catalogs`;
  const res = await oldHttp.get<ListFacebookBusinessCatalogsResponse>(url, {
    ...config,
    params: localParams,
  });
  return correctPagingCursor(res);
}

export const instagramProductTags = {
  async searchProducts(
    {
      brandId,
      params: {
        instagramUserId,
        query,
        catalogId,
        after,
      } = {} as Partial<GetBrandsBrandIdInstagramProductTagsSearchProductsParams>,
    }: {
      brandId: number | string;
      params?: Partial<GetBrandsBrandIdInstagramProductTagsSearchProductsParams>;
    },
    config: HttpRequestConfig = {},
  ) {
    const url = `/brands/${brandId}/instagram_product_tags/search_products`;
    return http.get<InstagramProductSearchResponse>(url, {
      ...config,
      params: { instagramUserId, query, catalogId, after },
    });
  },
  async searchProductsByIds(
    {
      brandId,
      params: { productIds } = { productIds: [] },
    }: {
      brandId: number | string;
      params?: { productIds: string[] };
    },
    config: HttpRequestConfig = {},
  ) {
    const url = `/brands/${brandId}/instagram_product_tags/products`;
    return http.get<InstagramProduct[]>(url, {
      ...config,
      params: { productIds: productIds.join(',') },
    });
  },
};

export async function getAccessToken(
  {
    brandId,
    requiredType = 'user' as GetAccessTokensRequiredType,
    requiredScopes = [] as string[],
  }: {
    brandId: number;
    requiredType?: GetAccessTokensRequiredType;
    requiredScopes?: string[];
  },
  config: HttpRequestConfig = {},
) {
  return http.get<AccessToken>('/access_tokens', {
    ...config,
    params: { brandId, requiredType, requiredScopes: requiredScopes.join(',') },
  });
}

export async function getInstagramProductTagsEligibility(
  { brandId }: { brandId: number | string },
  config: HttpRequestConfig = {},
) {
  return oldHttp.get<InstagramShoppingTagsEligibilityResponse>(
    getGetBrandsBrandIdInstagramProductTagsEligibilityUrl(Number(brandId)),
    config,
  );
}

export async function getInstagramProductTagsCatalogs(
  {
    brandId,
    instagramUserId,
  }: {
    brandId: number | string;
    instagramUserId: GetBrandsBrandIdInstagramProductTagsCatalogsParams['instagramUserId'];
  },
  config: HttpRequestConfig = {},
) {
  return oldHttp.get<InstagramShoppingTagsProductCatalogResponse[]>(
    `/brands/${brandId}/instagram_product_tags/catalogs`,
    merge({ params: { instagramUserId } }, config),
  );
}

export const collateData = <T extends Record<string, unknown>>(
  currentValue: T[] | undefined,
  newValue: T[] | undefined,
  { uniqueOn, faultyNext = false }: { uniqueOn?: keyof T; faultyNext?: boolean } = {},
): T[] => {
  if ((currentValue ?? []).length === 0) {
    return newValue ?? [];
  }

  // Some facebook endpoints return a next when the next request returns an empty array
  if (!faultyNext && (newValue ?? []).length === 0) {
    return [];
  }

  // Facebook paging cannot be trusted not to have duplicates
  const combined = [...(currentValue ?? []), ...(newValue ?? [])];
  if (!uniqueOn) {
    return combined;
  }

  const uniqueIdentifiers = new Set(combined.map((obj) => obj[uniqueOn]));
  return Array.from(uniqueIdentifiers).map(
    (id) => combined.find((obj) => obj[uniqueOn] === id) as T,
  );
};

export async function getLocations(
  {
    brandId,
    query,
    locationType,
    locationFields,
    limit,
  }: {
    brandId: number | string;
    query?: string;
    locationType?: GetLocationsLocationType;
    locationFields?: GetLocationsLocationFieldsItem[];
    limit?: number;
  },
  config: HttpRequestConfig = {},
) {
  return oldHttp.get<Location[]>(
    `/locations`,
    merge(
      {
        params: {
          brandId,
          query,
          locationType,
          locationFields,
          limit,
        },
      },
      config,
    ),
  );
}

export async function getMarketingV2Campaigns(
  {
    adAccountIds,
    brandIds,
    sourceCampaignIds,
  }: {
    adAccountIds: string[];
    brandIds: number[];
    sourceCampaignIds?: string[];
  },
  config: HttpRequestConfig = {},
) {
  return http.put<AdCampaignsV2[]>(
    getPutBrandsMarketingV2CampaignsUrl(),
    {
      adAccountIds,
      brandIds,
      sourceCampaignIds,
    },
    merge({}, config),
  );
}

export async function searchMarketingV2Campaigns(
  {
    brandId,
    sourceAdAccountIds,
    effectiveStatuses,
    limit,
    offset,
    sort,
    search,
    startDate,
    endDate,
  }: {
    brandId: number | string;
    sourceAdAccountIds: string[];
    effectiveStatuses?: string[] | null;
    limit?: number;
    offset?: number;
    sort?: string | null;
    search?: string | null;
    startDate?: string | null;
    endDate?: string | null;
  },
  config: HttpRequestConfig = {},
) {
  return http.post<ListCampaignV2InsightsResponse>(
    getPostBrandsBrandIdMarketingV2CampaignsSearchUrl(Number(brandId)),
    {
      sourceAdAccountIds,
      effectiveStatuses,
      limit,
      offset,
      sort,
      search,
      startDate,
      endDate,
    },
    merge({}, config),
  );
}

export async function getMarketingV2Ads(
  {
    brandId,
    sourceCampaignIds,
    sourceAdIds,
    sort,
    search,
    effectiveStatuses,
    creativeTypes,
    limit = 100,
    offset = 0,
  }: {
    brandId: number | string;
    sourceCampaignIds?: string[];
    sourceAdIds?: string[];
    sort?: string;
    search?: string;
    effectiveStatuses?: string[];
    creativeTypes?: string[];
    limit?: number;
    offset?: number;
  },
  config: HttpRequestConfig = {},
) {
  return http.post<AdSearchResponse>(
    getPostBrandsBrandIdMarketingV2AdsSearchUrl(Number(brandId)),
    {
      sourceCampaignIds,
      sourceAdIds,
      sort,
      search,
      effectiveStatuses,
      creativeTypes,
      limit,
      offset,
    },
    merge({}, config),
  );
}

export async function createMarketingV2Ads(
  {
    brandId,
    sourceAdAccountId,
    ads,
  }: {
    brandId: number | string;
    sourceAdAccountId: string;
    ads: unknown[];
  },
  config: HttpRequestConfig = {},
) {
  return http.post<AdsCreationResponse>(
    getPostBrandsBrandIdMarketingV2AdsUrl(Number(brandId)),
    {
      sourceAdAccountId,
      ads,
    },
    merge({}, config),
  );
}

export async function sendImagesToMetaAds(
  {
    brandId,
    adAccountIds,
    imageUrls,
  }: {
    brandId: number | string;
    adAccountIds: string[];
    imageUrls: string[];
  },
  config: HttpRequestConfig = {},
) {
  return http.post<AdImagesUploadResponse>(
    getPostBrandsBrandIdMarketingV2AdimagesUrl(Number(brandId)),
    {
      adAccountIds,
      imageUrls,
    },
    merge({}, config),
  );
}

export async function sendVideosToMetaAds(
  {
    brandId,
    adAccountIds,
    videoUrls,
  }: {
    brandId: number | string;
    adAccountIds: string[];
    videoUrls: string[];
  },
  config: HttpRequestConfig = {},
) {
  return http.post<AdVideosUploadResponse>(
    getPostBrandsBrandIdMarketingV2AdvideosUrl(Number(brandId)),
    {
      adAccountIds,
      videoUrls,
    },
    merge({}, config),
  );
}

export async function getMetaAdVideoStatus(
  { brandId, videoId }: { brandId: number | string; videoId: string },
  config: HttpRequestConfig = {},
) {
  return http.get<AdVideoStatusResponse>(
    getGetBrandsBrandIdMarketingV2AdvideosVideoIdStatusUrl(Number(brandId), videoId),
    merge({}, config),
  );
}

export async function createMarketingV2VideoAds(
  {
    brandId,
    sourceAdAccountId,
    ads,
  }: {
    brandId: number | string;
    sourceAdAccountId: string;
    ads: unknown[];
  },
  config: HttpRequestConfig = {},
) {
  return http.post<VideoAdsCreationResponse>(
    getPostBrandsBrandIdMarketingV2VideoAdsUrl(Number(brandId)),
    {
      sourceAdAccountId,
      ads,
    },
    merge({}, config),
  );
}
